import { useEffect, useMemo, useState } from "react";
import { DEFAULT_PACKING } from "@/data/itinerary";
import { STORAGE_KEYS, storage } from "@/lib/storage";
import { Check, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

function keyOf(cat, item) {
  return `${cat}::${item}`;
}

export default function Checklist() {
  const [checked, setChecked] = useState({});
  const [extras, setExtras] = useState({}); // { cat: [item, ...] }
  const [newItemCat, setNewItemCat] = useState(DEFAULT_PACKING[0].cat);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    setChecked(storage.get(STORAGE_KEYS.checklist, {}));
    setExtras(storage.get(STORAGE_KEYS.extraItems, {}));
  }, []);

  const categories = useMemo(() => {
    return DEFAULT_PACKING.map((c) => ({
      cat: c.cat,
      items: [...c.items, ...(extras[c.cat] || [])],
    }));
  }, [extras]);

  const total = useMemo(
    () => categories.reduce((n, c) => n + c.items.length, 0),
    [categories]
  );
  const done = useMemo(
    () =>
      categories.reduce(
        (n, c) => n + c.items.filter((i) => checked[keyOf(c.cat, i)]).length,
        0
      ),
    [categories, checked]
  );
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = (cat, item) => {
    const k = keyOf(cat, item);
    const next = { ...checked, [k]: !checked[k] };
    setChecked(next);
    storage.set(STORAGE_KEYS.checklist, next);
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    const nextExtras = {
      ...extras,
      [newItemCat]: [...(extras[newItemCat] || []), newItem.trim()],
    };
    setExtras(nextExtras);
    storage.set(STORAGE_KEYS.extraItems, nextExtras);
    setNewItem("");
    toast.success(`"${newItem.trim()}" aggiunto a ${newItemCat}`);
  };

  const removeExtra = (cat, item) => {
    const nextExtras = {
      ...extras,
      [cat]: (extras[cat] || []).filter((i) => i !== item),
    };
    setExtras(nextExtras);
    storage.set(STORAGE_KEYS.extraItems, nextExtras);
    const nextChecked = { ...checked };
    delete nextChecked[keyOf(cat, item)];
    setChecked(nextChecked);
    storage.set(STORAGE_KEYS.checklist, nextChecked);
  };

  const resetAll = () => {
    setChecked({});
    storage.set(STORAGE_KEYS.checklist, {});
    toast.info("Checklist azzerata");
  };

  return (
    <div data-testid="checklist-page" className="pt-28 pb-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-accent text-xs text-[#D96C4A] mb-3">
              Prima di partire
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-[#1D3A4C]">
              Cosa mettere in valigia
            </h1>
            <p className="mt-3 text-[#5C7180] max-w-xl">
              Ogni voce si spunta con un click e resta salvata sul tuo browser.
            </p>
          </div>
          <button
            data-testid="checklist-reset"
            onClick={resetAll}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1D3A4C]/20 text-sm text-[#1D3A4C] hover:bg-[#1D3A4C]/5 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Azzera
          </button>
        </div>

        {/* Progress */}
        <div className="mb-14 p-6 rounded-2xl bg-white border border-[#1D3A4C]/10">
          <div className="flex items-center justify-between mb-3">
            <div className="font-accent text-xs text-[#8A9BA8]">
              Preparativi
            </div>
            <div
              data-testid="checklist-progress-label"
              className="font-display text-lg text-[#1D3A4C]"
            >
              {done} / {total}
            </div>
          </div>
          <div className="h-2 rounded-full bg-[#F5EFE6] overflow-hidden">
            <div
              data-testid="checklist-progress-bar"
              className="h-full bg-[#D96C4A] transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-[#8A9BA8]">
            {pct}% pronto per l'Elba
          </div>
        </div>

        {/* Categories */}
        <div className="grid md:grid-cols-2 gap-6">
          {categories.map((c) => (
            <div
              key={c.cat}
              data-testid={`checklist-category-${c.cat}`}
              className="p-6 rounded-2xl bg-white border border-[#1D3A4C]/10"
            >
              <div className="font-display text-2xl text-[#1D3A4C] mb-4">
                {c.cat}
              </div>
              <ul className="space-y-1">
                {c.items.map((item) => {
                  const k = keyOf(c.cat, item);
                  const isChecked = !!checked[k];
                  const isExtra = (extras[c.cat] || []).includes(item);
                  return (
                    <li
                      key={item}
                      className="group flex items-center justify-between gap-3 py-2"
                    >
                      <button
                        data-testid={`checklist-item-${c.cat}-${item}`}
                        onClick={() => toggle(c.cat, item)}
                        className="flex items-center gap-3 text-left flex-1 min-w-0"
                      >
                        <span
                          className={`w-5 h-5 rounded-md border shrink-0 flex items-center justify-center transition-all duration-200 ${
                            isChecked
                              ? "bg-[#D96C4A] border-[#D96C4A]"
                              : "border-[#1D3A4C]/30 group-hover:border-[#D96C4A]"
                          }`}
                        >
                          {isChecked && (
                            <Check
                              className="w-3 h-3 text-white"
                              strokeWidth={3}
                            />
                          )}
                        </span>
                        <span
                          className={`text-sm transition-all ${
                            isChecked
                              ? "line-through text-[#8A9BA8]"
                              : "text-[#1D3A4C]"
                          }`}
                        >
                          {item}
                        </span>
                      </button>
                      {isExtra && (
                        <button
                          data-testid={`checklist-remove-${c.cat}-${item}`}
                          onClick={() => removeExtra(c.cat, item)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-[#8A9BA8] hover:text-[#D96C4A]"
                          aria-label={`Rimuovi ${item}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Add item */}
        <div className="mt-14 p-6 rounded-2xl bg-[#F5EFE6] border border-[#1D3A4C]/10">
          <div className="font-accent text-xs text-[#D96C4A] mb-4">
            Aggiungi voce
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              data-testid="checklist-add-category"
              value={newItemCat}
              onChange={(e) => setNewItemCat(e.target.value)}
              className="px-4 py-3 rounded-full bg-white border border-[#1D3A4C]/15 text-sm text-[#1D3A4C] outline-none focus:border-[#D96C4A]"
            >
              {DEFAULT_PACKING.map((c) => (
                <option key={c.cat} value={c.cat}>
                  {c.cat}
                </option>
              ))}
            </select>
            <input
              data-testid="checklist-add-input"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addItem();
              }}
              placeholder="Es: pinne da snorkeling"
              className="flex-1 px-5 py-3 rounded-full bg-white border border-[#1D3A4C]/15 text-sm text-[#1D3A4C] outline-none focus:border-[#D96C4A] placeholder-[#8A9BA8]"
            />
            <button
              data-testid="checklist-add-button"
              onClick={addItem}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1D3A4C] hover:bg-[#0f2532] text-white text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
